import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api, API } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import {
  HeartHandshake, Download, Plus, X, Edit3, Trash2, RefreshCw,
  Mail, FileText, Megaphone, Share2,
} from "lucide-react";
import LogoUploader from "../../components/admin/LogoUploader";

const PALETTE = ["#FF00F5", "#0033FF", "#7B2CFF", "#00D9FF", "#FFB800", "#FF6B35", "#00C896"];
const GRID = "rgba(255,255,255,0.06)";
const AXIS = "rgba(255,255,255,0.5)";
const tt = {
  contentStyle: { background: "#0F0F0F", border: "1px solid #FF00F5", color: "#fff", fontSize: 12 },
  itemStyle: { color: "#fff" }, labelStyle: { color: "#FF00F5", marginBottom: 4 },
};
const LEGEND_STYLE = { fontSize: 11, color: "#fff" };
const BAR_RADIUS = [4, 4, 0, 0];

const DONOR_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "corporate", label: "Corporate" },
  { value: "general", label: "General" },
  { value: "scholarship", label: "Scholarship" },
  { value: "in_kind", label: "In-Kind (FMV)" },
];
const SOURCES = [
  { value: "zeffy", label: "Zeffy" },
  { value: "event", label: "Event" },
  { value: "website", label: "Website" },
  { value: "scholarship_campaign", label: "Scholarship Campaign" },
  { value: "other", label: "Other" },
];

const EMPTY = {
  donor_name: "", organization: "", logo_url: "", amount: 0, in_kind_fmv: 0,
  donor_type: "individual", donor_category: "", source: "website", campaign: "",
  donation_date: new Date().toISOString().slice(0, 10),
  recurring: false, recurring_frequency: "",
  public_recognition: true, anonymous: false, published: false,
  thank_you_sent: false, tax_letter_sent: false,
  social_media_recognized: false, press_release_recognized: false,
  notes: "",
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const TYPE_LABEL = Object.fromEntries(DONOR_TYPES.map((t) => [t.value, t.label]));
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.value, s.label]));

export default function DonationsPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [summary, setSummary] = useState(null);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ donor_type: "", source: "" });
  const [editing, setEditing] = useState(null); // null | "new" | <donation>

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/donations/summary", { withCredentials: true }),
        api.get("/admin/donations", { withCredentials: true, params: filter }),
      ]);
      setSummary(s.data);
      setDonations(d.data);
    } catch { toast.error("Failed to load donations"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/donations/export.csv`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "aawta-donations.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this donation? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/donations/${id}`, { withCredentials: true });
      toast.success("Donation deleted.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading || !summary) {
    return <p className="text-white/60" data-testid="donations-loading">Loading donations…</p>;
  }

  return (
    <div className="space-y-10" data-testid="donations-root">
      <div className="flex items-end justify-between flex-wrap gap-4 no-print">
        <div>
          <p className="text-overline text-aawta-pink mb-2 flex items-center gap-2"><HeartHandshake size={14} /> DONATIONS</p>
          <h1 className="font-display text-4xl text-white">Fundraising Command</h1>
          <p className="text-white/50 mt-2 text-sm">Track every gift, recognize every donor, report board-ready totals.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="donations-refresh-btn" onClick={load} className="btn-ghost py-2.5 px-4"><RefreshCw size={14} /> REFRESH</button>
          <button data-testid="donations-export-btn" onClick={exportCsv} className="btn-ghost py-2.5 px-4"><Download size={14} /> CSV</button>
          {editable && (
            <button data-testid="donations-add-btn" onClick={() => setEditing("new")} className="btn-pink"><Plus size={14} /> ADD DONATION</button>
          )}
        </div>
      </div>

      {/* --- Goal + KPI row --- */}
      <Section title="Donation Goal Progress" subtitle="Live progress towards the gala's standalone donation goal.">
        <GoalCard s={summary} />
      </Section>

      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="donations-kpis">
        <Kpi label="# of Donations" value={summary.count} sub={summary.recurring_count ? `${summary.recurring_count} recurring` : "one-time gifts"} />
        <Kpi label="Average Gift" value={fmt$(summary.average)} sub={`Largest: ${fmt$(summary.largest)}`} />
        <Kpi label="Smallest Gift" value={fmt$(summary.smallest)} sub="Cash donations" />
        <Kpi label="Recurring Total" value={fmt$(summary.recurring_total)} sub={`${summary.recurring_count} active subs`} />
        <Kpi label="In-Kind FMV" value={fmt$(summary.in_kind_total)} sub="Estimated value" accent />
      </section>

      {/* --- Donor Type & Source breakdowns --- */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Donation Breakdown" subtitle="By donor type.">
          <DonutCard data={summary.by_type} labelMap={TYPE_LABEL} testId="chart-donor-type" />
        </Section>
        <Section title="Donation Source" subtitle="Where the dollars are coming from.">
          <DonutCard data={summary.by_source} labelMap={SOURCE_LABEL} testId="chart-donation-source" />
        </Section>
      </div>

      {/* --- Reports --- */}
      <Section title="Reports & Trends" subtitle="Board-ready charts for fundraising reviews.">
        <div className="grid lg:grid-cols-2 gap-6">
          <ChartFrame testId="chart-by-month" empty={summary.by_month.length === 0} emptyMsg="No donations recorded yet — add a gift to populate the chart.">
            <p className="text-overline text-aawta-pink mb-3">DONATIONS BY MONTH</p>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={summary.by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="key" stroke={AXIS} fontSize={11} />
                <YAxis stroke={AXIS} fontSize={11} tickFormatter={fmt$} />
                <Tooltip {...tt} formatter={(v) => fmt$(v)} />
                <Line type="monotone" dataKey="amount" stroke="#FF00F5" strokeWidth={2} dot={{ fill: "#FF00F5", r: 3 }} name="Total" />
              </LineChart>
            </ResponsiveContainer>
          </ChartFrame>

          <ChartFrame testId="chart-by-campaign" empty={summary.by_campaign.length === 0} emptyMsg="No campaign data yet.">
            <p className="text-overline text-aawta-pink mb-3">DONATIONS BY CAMPAIGN</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={summary.by_campaign.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                <XAxis dataKey="key" stroke={AXIS} fontSize={11} interval={0} angle={-12} textAnchor="end" />
                <YAxis stroke={AXIS} fontSize={11} tickFormatter={fmt$} />
                <Tooltip {...tt} formatter={(v) => fmt$(v)} />
                <Bar dataKey="amount" fill="#0033FF" name="Raised" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </ChartFrame>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Section title="Top Donors" subtitle="Recognize your most generous supporters first.">
            <TopDonors donors={summary.top_donors} />
          </Section>
          <Section title="Year-to-Date" subtitle={`Total raised in ${new Date().getFullYear()}.`}>
            <div className="border border-aawta-pink/40 bg-aawta-pink/5 p-6">
              <p className="text-overline text-aawta-pink/80">YEAR-TO-DATE</p>
              <p className="font-display text-5xl text-white mt-3" data-testid="ytd-total">{fmt$(summary.ytd)}</p>
              <p className="text-xs text-white/50 mt-2">Includes in-kind FMV. Reset every January.</p>
            </div>
          </Section>
        </div>
      </Section>

      {/* --- Recent / All Donations Table --- */}
      <Section title="Recent Donations" subtitle="Full list filterable by donor type and source.">
        <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="donations-filters">
          <select data-testid="filter-donor-type" value={filter.donor_type} onChange={(e) => setFilter({ ...filter, donor_type: e.target.value })}
                  className="bg-aawta-charcoal/60 border border-white/10 text-white text-sm px-3 py-2">
            <option value="">All Donor Types</option>
            {DONOR_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select data-testid="filter-source" value={filter.source} onChange={(e) => setFilter({ ...filter, source: e.target.value })}
                  className="bg-aawta-charcoal/60 border border-white/10 text-white text-sm px-3 py-2">
            <option value="">All Sources</option>
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <p className="text-overline text-white/50 ml-auto">{donations.length} record(s)</p>
        </div>
        <DonationsTable donations={donations} editable={editable} onEdit={setEditing} onDelete={removeOne} />
      </Section>

      {editing && (
        <DonationModal
          initial={editing === "new" ? EMPTY : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-aawta-pink/40 bg-aawta-pink/5" : "border-white/10 bg-aawta-charcoal/40"}`}>
      <p className="text-overline text-white/50">{label}</p>
      <p className="font-display text-2xl text-white mt-2">{value}</p>
      <p className="text-xs text-white/40 mt-1">{sub}</p>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-display text-2xl text-white">{title}</h2>
        <p className="text-white/50 text-sm">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function ChartFrame({ children, testId, empty, emptyMsg }) {
  return (
    <div data-testid={testId} className="border border-white/10 bg-aawta-charcoal/30 p-4">
      {empty ? <div className="h-[260px] flex items-center justify-center text-white/40 text-sm">{emptyMsg}</div> : children}
    </div>
  );
}

function GoalCard({ s }) {
  const pct = Math.min(100, s.pct || 0);
  return (
    <div className="border border-aawta-pink/40 bg-aawta-pink/5 p-6" data-testid="donation-goal-card">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
        <div>
          <p className="text-overline text-aawta-pink">DONATION GOAL</p>
          <p className="font-display text-4xl text-white mt-1">{fmt$(s.raised)} <span className="text-white/40 text-lg">/ {fmt$(s.goal)}</span></p>
          <p className="text-xs text-white/50 mt-1">{fmt$(s.remaining)} remaining · {s.pct}% achieved</p>
        </div>
        <div className="text-right">
          <p className="text-overline text-white/50">In-Kind FMV</p>
          <p className="font-display text-2xl text-white mt-1">{fmt$(s.in_kind_total)}</p>
        </div>
      </div>
      <div className="h-3 bg-white/10 overflow-hidden">
        <div className="h-full bg-aawta-pink transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DonutCard({ data, labelMap, testId }) {
  const formatted = data.map((d) => ({ ...d, name: labelMap[d.key] || d.key }));
  return (
    <ChartFrame testId={testId} empty={formatted.length === 0} emptyMsg="No data yet.">
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={formatted} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={55} paddingAngle={2}>
            {formatted.map((row, i) => (
              <Cell key={row.name} fill={PALETTE[i % PALETTE.length]} stroke="#0A0A0A" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip {...tt} formatter={(v, n, p) => [`${fmt$(v)} · ${p.payload.count} gift(s)`, p.payload.name]} />
          <Legend wrapperStyle={LEGEND_STYLE} />
        </PieChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

function TopDonors({ donors }) {
  return (
    <div className="border border-white/10 bg-aawta-charcoal/30 divide-y divide-white/5" data-testid="top-donors-list">
      {donors.length === 0 ? (
        <p className="px-4 py-6 text-white/40 text-sm">No public donors yet.</p>
      ) : donors.map((d, i) => (
        <div key={d.name} data-testid={`top-donor-${i}`} className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-aawta-pink font-mono w-6">{i + 1}</span>
            <p className="text-sm text-white">{d.name}</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-aawta-pink">{fmt$(d.amount)}</p>
            <p className="text-xs text-white/40">{d.count} gift(s)</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function DonationsTable({ donations, editable, onEdit, onDelete }) {
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="donations-table">
      {donations.length === 0 ? (
        <p className="p-8 text-center text-white/40 text-sm">No donations match these filters yet.</p>
      ) : (
        <table className="w-full text-sm min-w-[1000px]">
        <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
          <tr>
            <th className="px-3 py-3">Donor</th>
            <th className="px-3 py-3">Type</th>
            <th className="px-3 py-3">Amount</th>
            <th className="px-3 py-3">Date</th>
            <th className="px-3 py-3">Campaign</th>
            <th className="px-3 py-3">Source</th>
            <th className="px-3 py-3">Recognition</th>
            {editable && <th className="px-3 py-3 w-24"></th>}
          </tr>
        </thead>
        <tbody>
          {donations.map((d) => {
            const amt = d.donor_type === "in_kind" ? d.in_kind_fmv : d.amount;
            return (
              <tr key={d.id} data-testid={`donation-row-${d.id}`} className="border-t border-white/5">
                <td className="px-3 py-2 text-white">
                  {d.anonymous ? <span className="text-white/40 italic">Anonymous</span> : d.donor_name}
                  {d.organization && <p className="text-xs text-white/40">{d.organization}</p>}
                </td>
                <td className="px-3 py-2 text-xs"><Pill color="#FF00F5">{TYPE_LABEL[d.donor_type] || d.donor_type}</Pill></td>
                <td className="px-3 py-2 text-aawta-pink font-mono">{fmt$(amt)}{d.recurring && <span className="text-xs text-white/40 ml-1">/{d.recurring_frequency || "mo"}</span>}</td>
                <td className="px-3 py-2 text-white/70 font-mono text-xs">{d.donation_date}</td>
                <td className="px-3 py-2 text-white/70 text-xs">{d.campaign || "—"}</td>
                <td className="px-3 py-2 text-white/70 text-xs">{SOURCE_LABEL[d.source] || d.source}</td>
                <td className="px-3 py-2">
                  <RecognitionFlags d={d} />
                </td>
                {editable && (
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button data-testid={`edit-donation-${d.id}`} onClick={() => onEdit(d)} title="Edit" className="text-white/50 hover:text-aawta-pink p-1"><Edit3 size={14} /></button>
                    <button data-testid={`delete-donation-${d.id}`} onClick={() => onDelete(d.id)} title="Delete" className="text-white/50 hover:text-red-400 p-1"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      )}
    </div>
  );
}

function Pill({ color, children }) {
  return <span className="inline-block px-2 py-0.5 text-[10px] tracking-widest uppercase" style={{ border: `1px solid ${color}55`, color }}>{children}</span>;
}

function RecognitionFlags({ d }) {
  const ICONS = [
    { flag: "thank_you_sent", icon: Mail, title: "Thank-you sent" },
    { flag: "tax_letter_sent", icon: FileText, title: "Tax letter sent" },
    { flag: "social_media_recognized", icon: Share2, title: "Social media post" },
    { flag: "press_release_recognized", icon: Megaphone, title: "Press release" },
  ];
  return (
    <div className="flex items-center gap-1.5">
      {d.anonymous && <Pill color="#888">ANONYMOUS</Pill>}
      {!d.public_recognition && !d.anonymous && <Pill color="#888">PRIVATE</Pill>}
      {ICONS.map(({ flag, icon: Icon, title }) => (
        <span key={flag} title={title} className={d[flag] ? "text-aawta-pink" : "text-white/15"}>
          <Icon size={13} />
        </span>
      ))}
    </div>
  );
}

/* ------------------- Modal ------------------- */
function DonationModal({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      ["amount", "in_kind_fmv"].forEach((k) => { body[k] = Number(body[k]) || 0; });
      if (isNew) {
        await api.post("/admin/donations", body, { withCredentials: true });
        toast.success("Donation recorded.");
      } else {
        await api.patch(`/admin/donations/${initial.id}`, body, { withCredentials: true });
        toast.success("Donation updated.");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 no-print" data-testid="donation-modal">
      <div className="bg-[#0F0F0F] border border-aawta-pink/40 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0F0F0F]">
          <h2 className="font-display text-2xl text-white">{isNew ? "Add Donation" : "Edit Donation"}</h2>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="modal-close-btn"><X size={18} /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <Input label="Donor Name *" value={form.donor_name} onChange={(v) => set("donor_name", v)} testId="form-donor-name" />
          <Input label="Organization" value={form.organization} onChange={(v) => set("organization", v)} testId="form-organization" />
          <Select label="Donor Type" value={form.donor_type} onChange={(v) => set("donor_type", v)} options={DONOR_TYPES} testId="form-donor-type" />
          <Select label="Source" value={form.source} onChange={(v) => set("source", v)} options={SOURCES} testId="form-source" />
          {form.donor_type === "in_kind" ? (
            <Input type="number" label="In-Kind FMV ($)" value={form.in_kind_fmv} onChange={(v) => set("in_kind_fmv", v)} testId="form-in-kind-fmv" />
          ) : (
            <Input type="number" label="Amount ($)" value={form.amount} onChange={(v) => set("amount", v)} testId="form-amount" />
          )}
          <Input type="date" label="Donation Date" value={form.donation_date} onChange={(v) => set("donation_date", v)} testId="form-donation-date" />
          <Input label="Campaign" value={form.campaign} onChange={(v) => set("campaign", v)} testId="form-campaign" placeholder="e.g. Power to Drive Change 2026" />
          <Input label="Recognition Category" value={form.donor_category} onChange={(v) => set("donor_category", v)} testId="form-donor-category" placeholder="e.g. Founders' Circle (optional)" />
          <div className="md:col-span-2">
            <LogoUploader label="Donor Logo (optional)" value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="form-logo" />
          </div>
          <div className="md:col-span-2 grid md:grid-cols-2 gap-3 pt-2 border-t border-white/5">
            <Toggle label="Recurring Donation" value={form.recurring} onChange={(v) => set("recurring", v)} testId="form-recurring" />
            {form.recurring && (
              <Select label="Frequency" value={form.recurring_frequency || "monthly"} onChange={(v) => set("recurring_frequency", v)}
                      options={[{ value: "monthly", label: "Monthly" }, { value: "quarterly", label: "Quarterly" }, { value: "annual", label: "Annual" }]} testId="form-recurring-frequency" />
            )}
            <Toggle label="Public Recognition" value={form.public_recognition} onChange={(v) => set("public_recognition", v)} testId="form-public-recognition" />
            <Toggle label="Anonymous" value={form.anonymous} onChange={(v) => set("anonymous", v)} testId="form-anonymous" />
            <Toggle label="Publish on Donor Honor Roll" value={form.published} onChange={(v) => set("published", v)} testId="form-published" />
          </div>
          <div className="md:col-span-2 grid md:grid-cols-2 gap-3 pt-2 border-t border-white/5">
            <p className="md:col-span-2 text-overline text-white/50">Recognition Tracking</p>
            <Toggle label="Thank-You Sent" value={form.thank_you_sent} onChange={(v) => set("thank_you_sent", v)} testId="form-thank-you" />
            <Toggle label="Tax Letter Sent" value={form.tax_letter_sent} onChange={(v) => set("tax_letter_sent", v)} testId="form-tax-letter" />
            <Toggle label="Social Media Recognition" value={form.social_media_recognized} onChange={(v) => set("social_media_recognized", v)} testId="form-social-media" />
            <Toggle label="Press Release Recognition" value={form.press_release_recognized} onChange={(v) => set("press_release_recognized", v)} testId="form-press-release" />
          </div>
          <div className="md:col-span-2">
            <Input label="Notes" value={form.notes || ""} onChange={(v) => set("notes", v)} testId="form-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#0F0F0F]">
          <button onClick={onClose} className="btn-ghost py-2.5 px-4">CANCEL</button>
          <button data-testid="donation-save-btn" onClick={save} disabled={saving || !form.donor_name} className="btn-pink disabled:opacity-50">
            {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <label className="block">
      <span className="text-overline text-white/50 mb-1.5 block">{label}</span>
      <input data-testid={testId} type={type} value={value ?? ""} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm" />
    </label>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="text-overline text-white/50 mb-1.5 block">{label}</span>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer">
      <input data-testid={testId} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
             className="accent-aawta-pink h-4 w-4" />
      {label}
    </label>
  );
}
